import { useMemo, useState } from 'react';
import type { CondutorLookup } from './types';

interface Props {
  condutores: CondutorLookup[];
  /** Preenchido quando a lista NÃO pôde ser recortada — diz qual documento falta. */
  avisoEscopo: string | null;
  idCondutorSgtf: string | null;
  onCondutor: (c: CondutorLookup) => void;
}

/** A partir de quantos itens a lista precisa de busca para ser usável. */
const LIMIAR_BUSCA = 12;

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase();
}

function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Passo 1 — quem vai dirigir.
 *
 * A lista vem recortada ao grupo financeiro do cooperado. Quando o vínculo de
 * CPF não existe — 27% deles são pessoa jurídica, e `condutor_sgtf` só guarda
 * CPF de pessoa física — ela chega inteira, com um aviso que nomeia o documento
 * ausente. Daí a busca: 275 botões-radio sem filtro não se usam.
 */
export function PassoCondutor({ condutores, avisoEscopo, idCondutorSgtf, onCondutor }: Props) {
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return condutores;
    // Só compara CPF quando o termo TEM dígito: `includes('')` é sempre
    // verdadeiro, e por isso buscar um nome inexistente devolvia os 275 em vez
    // do estado "nenhum encontrado".
    const digitos = termo.replace(/\D/g, '');
    return condutores.filter(c =>
      normalizar(c.nome).includes(termo)
      || (digitos.length > 0 && (c.cpf ?? '').replace(/\D/g, '').includes(digitos)),
    );
  }, [condutores, busca]);

  return (
    <div className="co-pilha">
      <p className="co-passo-apoio">O condutor define a frota disponível no próximo passo.</p>

      {avisoEscopo && <p className="co-nota">{avisoEscopo}</p>}

      {condutores.length > LIMIAR_BUSCA && (
        <label className="co-campo">
          <span className="co-campo-rotulo">Buscar condutor</span>
          <input
            className="co-entrada"
            type="search"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Nome ou CPF"
          />
        </label>
      )}

      <fieldset className="co-opcoes">
        <legend className="sr-only">Condutor</legend>
        {filtrados.map(c => (
          <label key={c.idCondutorSgtf} className="co-opcao">
            <input
              type="radio"
              name="condutor"
              value={c.idCondutorSgtf}
              checked={idCondutorSgtf === c.idCondutorSgtf}
              onChange={() => onCondutor(c)}
            />
            <span className="co-opcao-marca" aria-hidden="true">{iniciais(c.nome)}</span>
            <span className="co-opcao-texto">
              <span className="co-opcao-titulo">{c.nome}</span>
              <span className="co-opcao-apoio">CNH {c.cnh}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {condutores.length > 0 && filtrados.length === 0 && (
        <p className="co-apoio" role="status">Nenhum condutor encontrado para "{busca}".</p>
      )}

      {condutores.length === 0 && (
        <p className="co-nota co-nota-atencao">Nenhum condutor disponível. Fale com a cooperativa.</p>
      )}
    </div>
  );
}
